import { ConfigProvider } from "antd";
import moment from "moment";
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { describeLocale, detectLocale, LocaleDescriptor, LOCALES } from "./locales";
import { Locale, MessageKey, Messages, MessageVars, PluralMessage } from "./types";
export type { Locale, MessageKey, MessageVars } from "./types";
export type { LocaleDescriptor } from "./locales";
export { LOCALES } from "./locales";

/** Remembers the chosen language between sessions; it is a workspace preference. */
const LOCALE_KEY = "locale";

/**
 * The two settings that live outside React: `moment` is a module-level
 * singleton, and `lang` is read by screen readers and the browser's own
 * spell-checking.
 */
function applyLocale(descriptor: LocaleDescriptor): void {
    document.documentElement.lang = descriptor.tag;
    moment.locale(descriptor.moment);
}

export type Translate = (key: MessageKey, vars?: MessageVars) => string;

export interface I18nValue {
    locale: Locale;
    /** Descriptor of the active locale — its own name, BCP 47 tag, and so on. */
    current: LocaleDescriptor;
    locales: LocaleDescriptor[];
    setLocale: (locale: Locale) => void;
    t: Translate;
}

const I18nContext = createContext<I18nValue | undefined>(undefined);

const isPlural = (message: string | PluralMessage): message is PluralMessage =>
    typeof message !== "string";

/**
 * Picks the plural form for `count` using the language's own CLDR categories,
 * so Russian gets its one/few/many split without this module knowing the rule.
 * A category the translation does not define falls back to `other`.
 */
function selectPluralForm(message: PluralMessage, tag: string, count: number): string {
    const category = new Intl.PluralRules(tag).select(count);
    return message[category] ?? message.other;
}

/** Substitutes `{name}` placeholders; an unknown one is left as written. */
function interpolate(template: string, vars?: MessageVars): string {
    if (vars === undefined) return template;
    return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
        const value = vars[name];
        return value === undefined ? whole : String(value);
    });
}

function translateWith(messages: Messages, tag: string): Translate {
    return (key, vars) => {
        const message = messages[key];
        // Only reachable if a catalogue is edited outside the type system;
        // showing the key beats showing nothing at all.
        if (message === undefined) return key;

        const template = isPlural(message)
            ? selectPluralForm(message, tag, Number(vars?.count ?? 0))
            : message;

        return interpolate(template, vars);
    };
}

/**
 * Makes the interface translatable.
 *
 * It carries three things that have to move together: this app's own strings,
 * antd's built-in copy (pagination, sorter tooltips, date picker, empty
 * states), and moment's month and weekday names. Switching the language from
 * the topbar has to change all three or the screen ends up half-translated.
 */
export const I18nProvider = ({ children }: { children: ReactNode }) => {
    const [locale, setLocaleState] = useState<Locale>(() => {
        const initial = detectLocale(localStorage.getItem(LOCALE_KEY));
        // Applied here, not in the mount effect: a child's effect runs before
        // its parent's, so a date formatted while a child mounts would
        // otherwise still be formatted in the previous language.
        applyLocale(describeLocale(initial));
        return initial;
    });

    const current = describeLocale(locale);

    const setLocale = useCallback((next: Locale) => {
        localStorage.setItem(LOCALE_KEY, next);
        setLocaleState(next);
    }, []);

    useEffect(() => applyLocale(current), [current]);

    const value = useMemo<I18nValue>(
        () => ({
            locale,
            current,
            locales: LOCALES,
            setLocale,
            t: translateWith(current.messages, current.tag)
        }),
        [locale, current, setLocale]
    );

    return (
        <I18nContext.Provider value={value}>
            <ConfigProvider locale={current.antd}>{children}</ConfigProvider>
        </I18nContext.Provider>
    );
};

export function useI18n(): I18nValue {
    const value = useContext(I18nContext);
    if (value === undefined) {
        throw new Error("useI18n must be used inside <I18nProvider>");
    }
    return value;
}

/** Shorthand for the common case of needing only the translate function. */
export function useTranslate(): Translate {
    return useI18n().t;
}
